import * as React from 'react';
import { makeStyles, Tooltip } from '@fluentui/react-components';
import { GlobeRegular } from '@fluentui/react-icons';
import { ReferenceV2 as Reference, ReferenceListV2 as ReferenceList, ReferenceOverflowButton } from '@fluentui-copilot/react-reference';
import { COPILOT_CONSTANTS } from './constants';

// Import centralized interfaces
import type { CopilotReferenceSource, CopilotMessageReferenceProps } from './interfaces';

const useStyles = makeStyles({
  container: {
    marginTop: '12px'
  },
  heading: {
    fontSize: '13px',
    color: '#323130',
    marginBottom: '8px',
    display: 'block'
  },
  list: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  link: {
    textDecoration: 'none',
    color: '#0078d4',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    maxWidth: '240px',
    fontSize: '12px',
    cursor: 'pointer',
    ':hover': { textDecoration: 'underline' }
  },
  icon: {
    width: '14px',
    height: '14px',
    flexShrink: 0,
    flexGrow: 0
  },
  itemInner: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    flex: '1 1 auto',
    maxWidth: '200px',
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '12px'
  }
});

const CopilotMessageReference: React.FC<CopilotMessageReferenceProps> = ({
  sources = [],
  messageId,
  heading = 'Sources:',
  maxSources = COPILOT_CONSTANTS.LIST_LIMITS.MAX_SOURCES,
  maxVisible = COPILOT_CONSTANTS.LIST_LIMITS.REFERENCE_V2_MAX_VISIBLE,
  minVisible = COPILOT_CONSTANTS.LIST_LIMITS.REFERENCE_V2_MIN_VISIBLE,
  className
}) => {
  const styles = useStyles();
  if (!sources || sources.length === 0) return null;

  // Cap total list first
  const capped = sources.slice(0, maxSources);

  return (
    <div className={`${styles.container} ${className || ''}`.trim()}>
      <strong className={styles.heading}>{heading}</strong>
      <ReferenceList
        className={styles.list}
        maxVisibleReferences={maxVisible}
        minVisibleReferences={minVisible}
        showMoreButton={<ReferenceOverflowButton>Show more</ReferenceOverflowButton>}
        showLessButton={<ReferenceOverflowButton>Show less</ReferenceOverflowButton>}
      >
        {capped.map((source, i) => {
          const label = source.title || `Source ${i + 1}`;
            const url = source.url || '#';
            const full = source.url || label;
            const linkEl = (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className={styles.link}               
              >
                <GlobeRegular className={styles.icon} />
                <span className={styles.itemInner}>{label}</span>
              </a>
            );
            return (
              <Reference key={`ref-${messageId}-${i}`} id={`ref-${messageId}-${i}`} aria-label={label}>
                {url !== '#' ? (
                  <Tooltip content={full} relationship="description">
                    {linkEl}
                  </Tooltip>
                ) : linkEl}
              </Reference>
            );
        })}
      </ReferenceList>
    </div>
  );
};

export default CopilotMessageReference;